import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { formatCountCompact } from 'utils/formatting';
import './ReviewTabs.scss';

const VERIFIED_TAB = 'verified';
const GOOGLE_TAB = 'google';

export function ReviewTabs({
	verifiedCount = 0,
	googleCount = 0,
	verifiedContent,
	googleContent,
	defaultTab,
}) {
	const hasGoogle = googleCount > 0;
	const [activeTab, setActiveTab] = useState(() => {
		if (defaultTab) return defaultTab;
		if (verifiedCount > 0) return VERIFIED_TAB;
		if (hasGoogle) return GOOGLE_TAB;
		return VERIFIED_TAB;
	});

	const verifiedTabRef = useRef(null);
	const googleTabRef = useRef(null);

	const handleToggle = (tab) => () => {
		if (tab === activeTab) return;
		setActiveTab(tab);
	};

	const handleKeyDown = (tab) => (e) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			handleToggle(tab)();
		} else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
			e.preventDefault();
			const nextTab = tab === VERIFIED_TAB ? GOOGLE_TAB : VERIFIED_TAB;
			const nextRef =
				nextTab === VERIFIED_TAB ? verifiedTabRef : googleTabRef;
			setActiveTab(nextTab);
			nextRef.current?.focus();
		}
	};

	return (
		<div className="review-tabs">
			{hasGoogle && (
				<div
					className="review-tabs__toggle"
					role="tablist"
				>
					<div
						ref={verifiedTabRef}
						className={`review-tabs__tab${activeTab === VERIFIED_TAB ? ' review-tabs__tab--active' : ''}`}
						onClick={handleToggle(VERIFIED_TAB)}
						role="tab"
						tabIndex={activeTab === VERIFIED_TAB ? 0 : -1}
						aria-selected={activeTab === VERIFIED_TAB}
						onKeyDown={handleKeyDown(VERIFIED_TAB)}
					>
						{`Verified (${verifiedCount})`}
					</div>
					<div
						ref={googleTabRef}
						className={`review-tabs__tab${activeTab === GOOGLE_TAB ? ' review-tabs__tab--active' : ''}`}
						onClick={handleToggle(GOOGLE_TAB)}
						role="tab"
						tabIndex={activeTab === GOOGLE_TAB ? 0 : -1}
						aria-selected={activeTab === GOOGLE_TAB}
						onKeyDown={handleKeyDown(GOOGLE_TAB)}
					>
						{`Google (${formatCountCompact(googleCount)})`}
					</div>
				</div>
			)}

			<div
				className={`review-tabs__content${activeTab === VERIFIED_TAB ? ' review-tabs__content--active' : ''}`}
				role="tabpanel"
				aria-hidden={activeTab !== VERIFIED_TAB}
			>
				{verifiedContent}
			</div>
			{hasGoogle && (
				<div
					className={`review-tabs__content${activeTab === GOOGLE_TAB ? ' review-tabs__content--active' : ''}`}
					role="tabpanel"
					aria-hidden={activeTab !== GOOGLE_TAB}
				>
					{googleContent}
				</div>
			)}
		</div>
	);
}

ReviewTabs.propTypes = {
	verifiedCount: PropTypes.number,
	googleCount: PropTypes.number,
	verifiedContent: PropTypes.node,
	googleContent: PropTypes.node,
	defaultTab: PropTypes.oneOf([VERIFIED_TAB, GOOGLE_TAB]),
};

export default ReviewTabs;
